import React, { useState, useEffect } from "react";

// ─── Eye Icon ────────────────────────────────────────────────────────────────
const EyeIcon = ({ open }) =>
  open ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );

// ─── Login ────────────────────────────────────────────────────────────────────
export default function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => { setTimeout(() => setMounted(true), 60); }, []);

  const handleLogin = async () => {
    if (!username || !password) { setError("Please fill in all fields."); return; }
    setError(""); setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      const data = await response.json();
      if (data.token) {
        localStorage.setItem("token", data.token);
        localStorage.setItem("username", data.username);
        localStorage.setItem("role", data.role);
        if (data.role === "admin" && new URLSearchParams(window.location.search).get("from") !== "adminhanuman") {
          setError("Access denied. Use the correct admin URL.");
          localStorage.removeItem("token");
          localStorage.removeItem("username");
          localStorage.removeItem("role");
          setLoading(false);
          return;
        }
        window.location.href = data.role === "admin" ? "/" : "/client";
      } else {
        setError(data.message || "Invalid credentials. Please try again.");
      }
    } catch { setError("Unable to connect. Please try again."); }
    finally { setLoading(false); }
  };

  const handleKey = (e) => { if (e.key === "Enter") handleLogin(); };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
        body{background:#f4f3f0;min-height:100vh;font-family:'Manrope',sans-serif;-webkit-font-smoothing:antialiased;}
        ::-webkit-scrollbar{width:6px;}::-webkit-scrollbar-thumb{background:rgba(0,0,0,0.12);border-radius:4px;}
        @keyframes spin{to{transform:rotate(360deg);}}
        @keyframes blob1{0%,100%{transform:translate(0,0) scale(1);}50%{transform:translate(30px,-20px) scale(1.08);}}
        @keyframes blob2{0%,100%{transform:translate(0,0) scale(1);}50%{transform:translate(-24px,26px) scale(1.05);}}

        .np-root{min-height:100vh;display:flex;}

        /* ── Left: brand panel ── */
        .np-left{
          flex:1.05;position:relative;display:flex;flex-direction:column;justify-content:space-between;
          padding:52px 56px;background:#17301f;color:#f3f6f1;overflow:hidden;
        }
        .np-blob{position:absolute;border-radius:50%;filter:blur(60px);opacity:0.5;}
        .np-blob1{width:380px;height:380px;background:#ffb648;top:-100px;left:-100px;animation:blob1 11s ease-in-out infinite;}
        .np-blob2{width:320px;height:320px;background:#5fd3a2;bottom:-110px;right:-80px;animation:blob2 13s ease-in-out infinite;}

        .np-mark{position:relative;z-index:1;display:flex;align-items:center;gap:12px;}
        .np-mark-icon{width:42px;height:42px;border-radius:12px;background:#ffb648;display:flex;align-items:center;justify-content:center;box-shadow:0 8px 20px rgba(255,182,72,0.35);}
        .np-mark-text{font-size:19px;font-weight:800;letter-spacing:-0.01em;}
        .np-mark-text span{display:block;font-size:11px;font-weight:500;letter-spacing:0.08em;text-transform:uppercase;color:rgba(243,246,241,0.55);margin-top:2px;}

        .np-hero{position:relative;z-index:1;max-width:400px;}
        .np-hero h2{font-size:32px;font-weight:800;line-height:1.25;letter-spacing:-0.01em;margin-bottom:14px;}
        .np-hero p{font-size:14.5px;line-height:1.65;color:rgba(243,246,241,0.68);}

        .np-pills{position:relative;z-index:1;display:flex;flex-direction:column;gap:10px;margin-top:28px;}
        .np-pill{display:flex;align-items:center;gap:10px;font-size:13px;color:rgba(243,246,241,0.85);}
        .np-pill-dot{width:7px;height:7px;border-radius:50%;background:#ffb648;flex-shrink:0;}

        .np-left-footer{position:relative;z-index:1;font-size:12px;color:rgba(243,246,241,0.4);}

        /* ── Right: form panel ── */
        .np-right{flex:0.95;display:flex;align-items:center;justify-content:center;background:#f4f3f0;padding:40px;}
        .np-form-wrap{
          width:100%;max-width:380px;
          transform:translateY(${mounted ? "0" : "16px"});
          opacity:${mounted ? 1 : 0};
          transition:transform 0.6s cubic-bezier(0.22,1,0.36,1),opacity 0.6s ease;
        }

        .np-form-head{margin-bottom:34px;}
        .np-form-head h1{font-size:25px;font-weight:800;color:#1c1917;letter-spacing:-0.01em;margin-bottom:6px;}
        .np-form-head p{font-size:13.5px;color:#78716c;}

        .np-field{margin-bottom:17px;}
        .np-label{display:block;font-size:12px;font-weight:600;color:#44403c;margin-bottom:7px;}

        .np-input-wrap{position:relative;border-radius:12px;background:#fff;border:1.5px solid #e7e5e4;transition:border-color 0.18s,box-shadow 0.18s;}
        .np-input-wrap.is-focused{border-color:#ffb648;box-shadow:0 0 0 4px rgba(255,182,72,0.15);}

        .np-input{width:100%;background:transparent;border:none;outline:none;padding:12.5px 15px;font-size:14px;font-family:'Manrope',sans-serif;color:#1c1917;}
        .np-input::placeholder{color:#a8a29e;}

        .np-eye{position:absolute;right:13px;top:50%;transform:translateY(-50%);background:none;border:none;cursor:pointer;color:#a8a29e;padding:2px;display:flex;align-items:center;transition:color 0.15s;}
        .np-eye:hover{color:#57534e;}

        .np-error{background:#fef2f2;border:1px solid #fecaca;border-radius:10px;padding:10px 13px;font-size:12.5px;color:#b91c1c;margin-bottom:18px;display:flex;align-items:center;gap:8px;}

        .np-btn{
          width:100%;padding:13.5px;border:none;border-radius:12px;cursor:pointer;margin-top:6px;
          font-family:'Manrope',sans-serif;font-size:14.5px;font-weight:700;letter-spacing:0.01em;color:#1c1917;
          background:#ffb648;
          box-shadow:0 6px 18px rgba(255,182,72,0.35);
          transition:transform 0.2s,box-shadow 0.2s,opacity 0.15s;
        }
        .np-btn:hover:not(:disabled){transform:translateY(-1px);box-shadow:0 10px 24px rgba(255,182,72,0.45);}
        .np-btn:active:not(:disabled){transform:translateY(0);}
        .np-btn:disabled{opacity:0.55;cursor:not-allowed;transform:none;}

        .np-btn-inner{display:flex;align-items:center;justify-content:center;gap:8px;}
        .np-spinner{width:15px;height:15px;border:2px solid rgba(28,25,23,0.25);border-top-color:#1c1917;border-radius:50%;animation:spin 0.7s linear infinite;}

        .np-footer{margin-top:26px;text-align:center;font-size:11.5px;color:#a8a29e;}

        @media (max-width:860px){
          .np-left{display:none;}
          .np-right{flex:1;}
        }
      `}</style>

      <div className="np-root">
        <div className="np-left">
          <div className="np-blob np-blob1" />
          <div className="np-blob np-blob2" />

          <div className="np-mark">
            <div className="np-mark-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#17301f" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2l3 7h7l-5.5 4.5L18 21l-6-4.5L6 21l1.5-7.5L2 9h7z" />
              </svg>
            </div>
            <div className="np-mark-text">
              Meridian
              <span>Control Center</span>
            </div>
          </div>

          <div className="np-hero">
            <h2>Run your operation from one calm screen.</h2>
            <p>Campaigns, clients and payouts — all in a workspace built to stay out of your way.</p>
            <div className="np-pills">
              <div className="np-pill"><span className="np-pill-dot" />Live campaign monitoring</div>
              <div className="np-pill"><span className="np-pill-dot" />Client & fund management</div>
              <div className="np-pill"><span className="np-pill-dot" />Broadcast announcements</div>
            </div>
          </div>

          <div className="np-left-footer">© 2026 Meridian</div>
        </div>

        <div className="np-right">
          <div className="np-form-wrap">
            <div className="np-form-head">
              <h1>Sign in</h1>
              <p>Enter your credentials to access your workspace.</p>
            </div>

            <div className="np-field">
              <label className="np-label">Username</label>
              <div className={`np-input-wrap${focused === "user" ? " is-focused" : ""}`}>
                <input
                  className="np-input"
                  type="text"
                  placeholder="Enter your username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  onKeyDown={handleKey}
                  onFocus={() => setFocused("user")}
                  onBlur={() => setFocused(null)}
                  autoComplete="username"
                />
              </div>
            </div>

            <div className="np-field">
              <label className="np-label">Password</label>
              <div className={`np-input-wrap${focused === "pass" ? " is-focused" : ""}`}>
                <input
                  className="np-input"
                  type={showPass ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={handleKey}
                  onFocus={() => setFocused("pass")}
                  onBlur={() => setFocused(null)}
                  autoComplete="current-password"
                  style={{ paddingRight: "44px" }}
                />
                <button
                  className="np-eye"
                  type="button"
                  onClick={() => setShowPass((v) => !v)}
                  tabIndex={-1}
                  aria-label={showPass ? "Hide password" : "Show password"}
                >
                  <EyeIcon open={showPass} />
                </button>
              </div>
            </div>

            {error && (
              <div className="np-error">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}>
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                {error}
              </div>
            )}

            <button className="np-btn" onClick={handleLogin} disabled={loading}>
              <span className="np-btn-inner">
                {loading && <span className="np-spinner" />}
                {loading ? "Signing in…" : "Sign In"}
              </span>
            </button>

            <div className="np-footer">Protected workspace · Meridian</div>
          </div>
        </div>
      </div>
    </>
  );
}
